
import { cn } from "@/lib/utils";
import { User, Bot } from "lucide-react";
import { useState } from "react";
import { useVoicePlayback } from "@/hooks/chat/useVoicePlayback";
import { MessageContent } from "./MessageContent";
import { MessageActions } from "./MessageActions";
import { MessageTimestamp } from "./MessageTimestamp";

export type MessageType = {
  id: string;
  content: string;
  role: "user" | "assistant";
  timestamp: Date;
  audioUrl?: string;
  read?: boolean;
};

interface ChatMessageProps {
  message: MessageType;
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === "user";
  const [feedbackGiven, setFeedbackGiven] = useState<"like" | "dislike" | null>(null);
  const { isPlaying, playMessage } = useVoicePlayback();

  const handleFeedback = (type: "like" | "dislike") => {
    setFeedbackGiven(type);
  };

  const toggleAudio = () => {
    playMessage(message.id, message.audioUrl);
  };

  return (
    <div className={cn(
      "flex gap-3 group relative mb-4",
      isUser ? "justify-end" : "justify-start"
    )}>
      {!isUser && (
        <div className="flex-shrink-0 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Bot size={28} className="text-primary" />
        </div>
      )}
      
      <div className={cn(
        "flex-1 max-w-[85%]",
        isUser && "flex justify-end"
      )}>
        <div className={cn(
          isUser 
            ? "text-foreground ml-auto" 
            : "rounded-2xl px-4 py-3 shadow-sm bg-muted/50 border"
        )}>
          <MessageContent content={message.content} isUser={isUser} />
          <MessageTimestamp timestamp={message.timestamp} isUser={isUser} />
        </div>

        {message.role === "assistant" && (
          <MessageActions
            messageId={message.id}
            messageContent={message.content}
            audioUrl={message.audioUrl}
            feedbackGiven={feedbackGiven}
            isPlaying={isPlaying}
            onFeedback={handleFeedback}
            onToggleAudio={toggleAudio}
          />
        )}
      </div>
      
      {isUser && (
        <div className="flex-shrink-0 h-12 w-12 rounded-full bg-secondary flex items-center justify-center">
          <User size={20} className="text-secondary-foreground" />
        </div>
      )}
    </div>
  );
}
